(() => {
  'use strict';
  const fields = ['title', 'body', 'tag', 'product_type', 'vendor', 'variants.title', 'variants.sku', 'variants.barcode'];
  function words(value) {
    if (typeof value !== 'string' || value.length > 120 || /[\u0000-\u001f\u007f:()"\\*]/u.test(value)) return null;
    const terms = value.trim().split(/\s+/u).filter(Boolean);
    if (!terms.length || terms.length > 8 || terms.some(term => term.length > 64 || ['AND', 'OR', 'NOT'].includes(term) || term.startsWith('-'))) return null;
    return terms;
  }
  function buildQuery(value) {
    const terms = words(value);
    return terms ? terms.map(term => '(' + fields.map(field => field + ':"' + term + '"').join(' OR ') + ')').join(' AND ') : '';
  }
  function displayTerms(value) {
    if (typeof value !== 'string' || value.length > 4096 || !value.startsWith('(title:"')) return value;
    const terms = value.split(' AND ').map(group => group.slice(8).split('" OR body:"')[0]);
    const plain = terms.join(' ');
    return buildQuery(plain) === value ? plain : value;
  }
  function searchUrl(value, endpoint, current = '') {
    const query = buildQuery(value);
    if (!query) return null;
    const origin = globalThis.location?.origin || 'https://fixture.invalid';
    const url = new URL(endpoint, origin);
    if (url.origin !== origin || !url.pathname.endsWith('/search')) return null;
    const previous = new URLSearchParams(current);
    // Keep the native sort and unavailable-product choice. Filters are never
    // discarded to manufacture a result; regular eligibility checks them first.
    for (const key of ['sort_by', 'options[unavailable_products]', 'options[prefix]']) {
      if (previous.has(key)) url.searchParams.set(key, previous.get(key));
    }
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', query);
    return url;
  }
  async function nativeResults(value, endpoint, { signal, current = '' } = {}) {
    const url = searchUrl(value, endpoint, current);
    if (!url) return null;
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) return null;
    signal?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 7000);
    try {
      const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin' });
      // This is a single bounded read. A refusal, timeout or empty literal
      // result leaves the original native response intact; there is no retry.
      if (!response.ok || new URL(response.url || url.href).origin !== url.origin) return null;
      const html = await response.text();
      if (html.length > 1500000) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const title = typeof doc.title === 'string' ? doc.title.trim() : '';
      if (!title || title.length > 512 || /[\u0000-\u001f\u007f]/u.test(title)) return null;
      const roots = doc.querySelectorAll('[data-bp-native-search]');
      if (roots.length !== 1) return null;
      const root = roots[0];
      if (root.dataset.bpNativeQuery !== url.searchParams.get('q') || !/^(0|[1-9][0-9]*)$/.test(root.dataset.bpNativeCount || '')) return null;
      const count = Number(root.dataset.bpNativeCount);
      const cards = [...root.querySelectorAll('[data-bp-search-product]')];
      if (!Number.isSafeInteger(count) || count > 100000 || cards.length > 24 || cards.length > count || (count > 0 && !cards.length)) return null;
      const ids = new Set();
      for (const card of cards) {
        const id = card.dataset.bpSearchProduct;
        const link = new URL(card.dataset.bpSearchUrl || '', url.origin);
        if (!/^[1-9][0-9]*$/.test(id) || ids.has(id) || link.origin !== url.origin || !link.pathname.startsWith('/products/') || !card.dataset.bpSearchTitle) return null;
        ids.add(id);
      }
      return { root, cards, count, title, query: url.searchParams.get('q'), plain: displayTerms(url.searchParams.get('q')), url };
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
    }
  }
  async function regular(root) {
    if (root.dataset.bpLiteralEligible !== 'true' || root.dataset.bpLiteralAttempt) return;
    const value = root.dataset.bpNativeQuery;
    if (!buildQuery(value)) return;
    root.dataset.bpLiteralAttempt = 'started';
    const originalUrl = location.href;
    const result = await nativeResults(value, root.dataset.bpSearchEndpoint || globalThis.routes?.search_url || location.pathname, { current: location.search });
    // Do not install a result after the shopper has changed query, page, sort
    // or filters. The fresh native section carries its own exact count/facets.
    if (!result?.count || !root.isConnected || location.href !== originalUrl) return;
    const replacement = document.importNode(result.root, true);
    replacement.dataset.bpLiteralAttempt = 'complete';
    root.replaceWith(replacement);
    document.title = result.title;
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(replacement);
    // The native filter forms already contain the literal query. Keep their
    // history baseline coherent; displayTerms/Liquid keep the human input plain.
    history.replaceState(history.state, '', result.url.href);
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.searchParamsInitial = result.url.searchParams.toString();
      FacetFiltersForm.searchParamsPrev = result.url.searchParams.toString();
    }
    document.dispatchEvent(new CustomEvent('bp:literal-search-complete', { detail: { count: result.count } }));
  }
  async function predictive(markup, value, signal) {
    if (!buildQuery(value)) return markup;
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    if (doc.querySelector('[id^="predictive-search-option-product-"]')) return markup;
    const root = doc.querySelector('#predictive-search-results');
    if (!root) return markup;
    const result = await nativeResults(value, globalThis.routes?.search_url || '/search', { signal });
    if (!result?.count || signal?.aborted) return markup;
    let wrapper = root.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) {
      wrapper = doc.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
      root.prepend(wrapper);
    }
    wrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');
    const group = doc.createElement('div'); group.className = 'predictive-search__result-group';
    const heading = doc.createElement('h2'); heading.id = 'predictive-search-products';
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing'; heading.textContent = 'Products';
    const list = doc.createElement('ul'); list.id = 'predictive-search-results-products-list';
    list.className = 'predictive-search__results-list list-unstyled'; list.setAttribute('role', 'group'); list.setAttribute('aria-labelledby', heading.id);
    result.cards.slice(0, 6).forEach((card, index) => {
      const li = doc.createElement('li'); li.id = 'predictive-search-option-product-' + (index + 1);
      li.className = 'predictive-search__list-item'; li.setAttribute('role', 'option'); li.setAttribute('aria-selected', 'false');
      const link = doc.createElement('a'); link.href = card.dataset.bpSearchUrl;
      link.className = 'predictive-search__item predictive-search__item--link-with-thumbnail link link--text'; link.tabIndex = -1;
      const content = doc.createElement('div'); content.className = 'predictive-search__item-content';
      const name = doc.createElement('p'); name.className = 'predictive-search__item-heading h5'; name.textContent = card.dataset.bpSearchTitle;
      content.append(name);
      if (card.dataset.bpSearchPrice) { const price = doc.createElement('span'); price.className = 'price'; price.textContent = card.dataset.bpSearchPrice; content.append(price); }
      const nativeImage = card.querySelector('img[src]');
      if (nativeImage) {
        const imageUrl = new URL(nativeImage.getAttribute('src'), result.url.origin);
        if (imageUrl.protocol === 'https:' && (imageUrl.origin === result.url.origin || imageUrl.hostname === 'cdn.shopify.com')) {
          const image = doc.createElement('img'); image.src = imageUrl.href; image.alt = nativeImage.getAttribute('alt') || ''; image.width = 50;
          image.className = 'predictive-search__image'; link.append(image);
        }
      }
      link.append(content); li.append(link); list.append(li);
    });
    group.append(heading, list); wrapper.append(group);
    const counter = root.querySelector('[data-total-results]');
    if (counter) {
      const previous = Number(counter.dataset.totalResults) || 0;
      const shown = list.children.length;
      counter.dataset.totalResults = String(previous + shown);
      counter.textContent = (previous + shown) + ' results: ' + shown + ' products';
    }
    return doc.body.innerHTML;
  }
  globalThis.BPLiteralSearch = Object.freeze({ buildQuery, displayTerms, searchUrl, nativeResults, regular, predictive });
  if (typeof document !== 'undefined') {
    const start = () => document.querySelectorAll('[data-bp-native-search]').forEach(root => regular(root));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})();
